import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getEnvVariable } from '@appcharge/shared-ui';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import clarity from '@microsoft/clarity';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LogRocket from 'logrocket';
import { Toaster } from 'sonner';
import { UISliceState } from 'store/store.types';

import BlockOverlay from './components/BlockOverlay/BlockOverlay';
import DialogModalManager from './components/DialogModalManager/DialogModalManager';
import { LOG_ROCKET_KEY } from './conf';
import { msalConfig } from './config/msalConfig';
import AppRoutes from './routes/AppRoutes';
import { initMixpanel } from './utils/mixpanelClient';

import './style/theme.scss';

LogRocket.init(LOG_ROCKET_KEY);
clarity.init('seac7q7ttc');

initMixpanel();

LicenseInfo.setLicenseKey(
  (getEnvVariable('REACT_APP_DATA_GRID_KEY_NEW') as string) ||
  '26352ff0346454e6c919d47b90644e1cTz05ODYyNyxFPTE3NTg3MDk0NjgwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLFBWPVEzLTIwMjQsS1Y9Mg=='
);

const queryClient = new QueryClient();

const msalInstance = new PublicClientApplication(msalConfig);

function App() {
  const location = useLocation();
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536
      }
    },
    palette: {
      primary: {
        main: 'rgb(67, 56, 202)'
      },
      secondary: {
        main: '#26282B'
      },
      error: {
        main: '#FF7C7C'
      }
    }
  });

  const uiState = useSelector(({ ui }: { ui: UISliceState }) => ui);
  const isSignUpRoute = location.pathname.startsWith('/sign-up');
  const isInitProjectRoute = location.pathname.startsWith('/init-project');
  const isLoginRoute = location.pathname.startsWith('/login');

  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <div id="theme">
            {uiState.isTokenLoading && (
              <Stack
                width="100vw"
                height="100vh"
                justifyContent="center"
                alignItems="center"
                position="absolute"
                top="0"
                left="0"
                bgcolor="rgba(0, 0, 0, 0.8)"
                zIndex="1000"
                gap={2}
              >
                <CircularProgress
                  sx={{
                    color: 'white'
                  }}
                />
                <Typography variant="h5" color="white">
                  Logging you in...
                </Typography>
              </Stack>
            )}
            {!isSignUpRoute && !isInitProjectRoute && !isLoginRoute && (
              <BlockOverlay />
            )}
            <Toaster className="w-[388px] gap-3.5 pr-4 pb-4 !right-6 !bottom-6" expand={true} />
            <DialogModalManager />
            <AppRoutes />
          </div>
        </ThemeProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
}

export default App;
